import {
  format,
  isSameYear,
  isThisYear,
  isToday,
  isYesterday,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

const dateLocaleOptions = { locale: ptBR };

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, dateLocaleOptions);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat, dateLocaleOptions);
  if (!isSameYear(messageTime, now)) {
    return messageDate;
  }
  return messageDate;
};

const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';

const normalizeUnixDate = time => {
  const rawTime = String(time ?? '').trim();
  const numericTime = /^\d+(\.\d+)?$/.test(rawTime) ? Number(rawTime) : NaN;

  if (!Number.isFinite(numericTime)) {
    const parsedTime = Date.parse(rawTime);
    if (Number.isNaN(parsedTime)) return null;
    return new Date(parsedTime);
  }

  const date = new Date(
    numericTime > 1_000_000_000_000 ? numericTime : numericTime * 1000
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Returns the newest timestamp available for a conversation in Unix seconds.
 * Chatwoot can temporarily expose an older `last_non_activity_message` while
 * the live `timestamp` has already advanced. The UI must use the same newest
 * value for the card clock and for ordering the conversation list.
 * @param {Object} conversation - Conversation payload from the API/store.
 * @returns {number} Latest activity in Unix seconds, or 0 when unavailable.
 */
export const conversationActivityTimestamp = conversation => {
  const activityValues = [
    conversation?.last_non_activity_message?.created_at,
    conversation?.lastNonActivityMessage?.createdAt,
    conversation?.timestamp,
    conversation?.last_activity_at,
    conversation?.lastActivityAt,
    conversation?.created_at,
    conversation?.createdAt,
  ];

  return activityValues.reduce((latest, value) => {
    const normalized = (normalizeUnixDate(value)?.getTime() || 0) / 1000;
    return Math.max(latest, normalized);
  }, 0);
};

const brazilDateParts = value =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: BRAZIL_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(value)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );

const brazilDateKey = value => {
  const parts = brazilDateParts(value);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const brazilDateKeyAsUtc = key => {
  const [year, month, day] = key.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};

/**
 * Returns the WhatsApp-style day heading for a message.
 * Today and yesterday use short labels; older messages include the weekday
 * and Brazilian date so long histories remain easy to scan.
 */
export const whatsappMessageDayLabel = time => {
  const date = normalizeUnixDate(time);
  if (!date) return '';

  const messageKey = brazilDateKey(date);
  const todayKey = brazilDateKey(new Date());
  const dayDifference = Math.round(
    (brazilDateKeyAsUtc(todayKey) - brazilDateKeyAsUtc(messageKey)) /
      (24 * 60 * 60 * 1000)
  );

  if (dayDifference === 0) return 'Hoje';
  if (dayDifference === 1) return 'Ontem';

  const weekday = new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIME_ZONE,
    weekday: 'long',
  }).format(date);
  const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  const parts = brazilDateParts(date);

  return `${capitalizedWeekday}, ${parts.day}/${parts.month}/${parts.year}`;
};

/**
 * Formats message metadata like WhatsApp: today's messages show only HH:mm;
 * older messages show the local date and time. Chatwoot stores message times
 * as Unix seconds, while the business operates in Brasília time.
 * @param {number|string} time - Unix timestamp in seconds.
 * @returns {string} Localized message time.
 */
export const whatsappMessageTimestamp = time => {
  const date = normalizeUnixDate(time);
  if (!date) return '';

  const messageParts = brazilDateParts(date);
  const todayParts = brazilDateParts(new Date());
  const timeText = new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  if (
    messageParts.year === todayParts.year &&
    messageParts.month === todayParts.month &&
    messageParts.day === todayParts.day
  ) {
    return timeText;
  }

  return `${messageParts.day}/${messageParts.month}/${messageParts.year}, ${timeText}`;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  if (isToday(date)) return format(date, 'h:mm a', dateLocaleOptions);
  if (isYesterday(date)) return yesterdayLabel;
  if (isThisYear(date)) return format(date, 'MMM d', dateLocaleOptions);
  return format(date, 'MMM d, yyyy', dateLocaleOptions);
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    ...dateLocaleOptions,
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, dateLocaleOptions);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
